import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import {
  PAGE_REQUEST_DEFAULT_PAGE_NUMBER,
  PAGE_REQUEST_DEFAULT_PAGE_SIZE,
} from '@core/models/common.model';
import { EmployeeApiService } from './employee-api.service';
import { EmployeeInterventionFilter, EmployeeInterventionResponse } from './employee.model';

// The interventions of the employee whose page is open (the Interventions tab): server-side paging and filtering.
@Injectable({ providedIn: 'root' })
export class EmployeeInterventionsStore {
  private readonly api = inject(EmployeeApiService);

  readonly items = signal<EmployeeInterventionResponse[]>([]);
  readonly loading = signal(false);
  readonly filter = signal<EmployeeInterventionFilter>({});
  readonly pageNumber = signal(PAGE_REQUEST_DEFAULT_PAGE_NUMBER);
  readonly pageSize = signal(PAGE_REQUEST_DEFAULT_PAGE_SIZE);
  readonly totalCount = signal(0);
  readonly totalPages = signal(0);

  private employeeId: string | null = null;

  // Opening another employee starts again from the first page with no filters.
  open(employeeId: string): void {
    if (this.employeeId !== employeeId) {
      this.employeeId = employeeId;
      this.filter.set({});
      this.pageNumber.set(PAGE_REQUEST_DEFAULT_PAGE_NUMBER);
      this.items.set([]);
    }

    this.load();
  }

  setFilter(patch: Partial<EmployeeInterventionFilter>): void {
    this.filter.update((current) => ({ ...current, ...patch }));
    this.pageNumber.set(PAGE_REQUEST_DEFAULT_PAGE_NUMBER);
    this.load();
  }

  clearFilter(): void {
    this.filter.set({});
    this.pageNumber.set(PAGE_REQUEST_DEFAULT_PAGE_NUMBER);
    this.load();
  }

  setPage(pageNumber: number, pageSize: number): void {
    this.pageNumber.set(pageNumber);
    this.pageSize.set(pageSize);
    this.load();
  }

  load(): void {
    if (!this.employeeId) {
      return;
    }

    this.loading.set(true);
    this.api
      .getEmployeeInterventions(
        this.employeeId,
        { pageNumber: this.pageNumber(), pageSize: this.pageSize() },
        this.filter(),
      )
      .subscribe({
        next: (page) => {
          this.items.set(page.items);
          this.totalCount.set(page.totalCount);
          this.totalPages.set(page.totalPages);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.loading.set(false);
        },
        error: (_: HttpErrorResponse) => this.loading.set(false),
      });
  }
}
